import type {ShouldSkipLazyLoadType} from 'app/interface';
import {appEvents} from 'app/utils/appEvents';
import {getComponentConfiguration} from 'app/utils/config';
import {generateRandomString} from 'app/utils/hash/generateRandomString';
import {filterOutContainerDescendantComponents} from 'app/utils/initialize/filterOutContainerDescendantComponents';
import {initModule} from 'app/utils/initialize/initModule';
import type {FindAndInitComponentsParams} from 'app/utils/initialize/interface';
import {lazyInitModule} from 'app/utils/initialize/lazyInitModule';
import {triggerCustomEvent} from 'app/utils/jqueryNative';
import type {ComponentConfig} from 'interfaces/foundation/components/componentConfig';

export function findAndInitComponents({selector, context, components, lazyLoadPage, callback, gemSettings}: FindAndInitComponentsParams) {
    const notInitializedElements = filterOutContainerDescendantComponents(Array.from(context.querySelectorAll<HTMLElement>(selector)));
    notInitializedElements.forEach((element) => {
        const moduleName = element.dataset.gemModuleName;
        if (!moduleName) {
            return;
        }

        let config: ComponentConfig | undefined;
        try {
            config = getComponentConfiguration(element);
        } catch {
            // Keep `config` undefined
        }

        const key = generateRandomString();

        element.classList.remove('gem-module-not-initialized');
        const moduleItem = components[moduleName];
        if (!moduleItem) {
            console.warn(`Module "${moduleName}" not found.`);
            return;
        }

        const removePortalContent = moduleItem.removePortalContent ?? true;
        if (moduleItem.hasConfig && !config) {
            console.error(`Config is required for module "${moduleName}", but was not found`);
            return;
        }

        let loadingType: ShouldSkipLazyLoadType = 'LOAD_LAZY';

        if (lazyLoadPage) {
            loadingType = moduleItem.shouldSkipLazyLoad?.({element, config}) ?? 'LOAD_LAZY';
        }

        const ReactLazyComponent = moduleItem.reactLazyComponent?.({config, gemSettings});
        if (loadingType !== 'LOAD_LAZY' || !lazyLoadPage) {
            if (ReactLazyComponent) {
                callback({
                    element,
                    config,
                    ReactLazyComponent,
                    key,
                    removePortalContent,
                });
            } else if (moduleItem.import) {
                initModule(moduleItem, element, config, gemSettings);
            }
        } else {
            const lazyInit = () => {
                triggerCustomEvent(element, appEvents.componentInitializationStart, moduleName);

                if (ReactLazyComponent) {
                    callback({
                        element,
                        config,
                        ReactLazyComponent,
                        key,
                        removePortalContent,
                    });
                } else if (moduleItem.import) {
                    initModule(moduleItem, element, config, gemSettings);
                }
            };

            lazyInitModule({
                config,
                element,
                lazyInit,
                moduleName,
                shouldSkipLazyLoad: moduleItem.shouldSkipLazyLoad,
            });
        }
    });
}
